import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Edit3,
  Trash2,
  Search,
  UserRoundCog,
  Shield,
  User,
  AlertCircle,
} from "lucide-react";
import UserFormModal from "./UserFormModal";
import userService from "@/services/userService";
import { showError, showInfo } from "@/store/errorStore";

/**
 * Componente de gerenciamento de usuários
 * CRUD completo com listagem, criação, edição e exclusão
 */
export default function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  /**
   * Buscar todos os usuários
   */
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await userService.getAllUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(
        error.response?.data?.message || "Erro ao carregar usuários",
        "Erro de Carregamento"
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Abrir modal para criar novo usuário
   */
  const handleCreateUser = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  /**
   * Abrir modal para editar usuário
   */
  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  /**
   * Salvar usuário (criar ou atualizar)
   */
  const handleSaveUser = async (userData) => {
    setSavingUser(true);
    try {
      if (editingUser) {
        // Atualizar usuário existente
        await userService.updateUser(editingUser.id, userData);
        showInfo(
          `Usuário "${userData.email}" atualizado com sucesso`,
          "Usuário Atualizado"
        );
      } else {
        // Criar novo usuário
        await userService.createUser(userData);
        showInfo(
          `Usuário "${userData.email}" criado com sucesso`,
          "Usuário Criado"
        );
      }

      setShowModal(false);
      setEditingUser(null);
      await fetchUsers();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erro ao salvar usuário";
      showError(errorMessage, "Erro");
    } finally {
      setSavingUser(false);
    }
  };

  /**
   * Confirmar exclusão de usuário
   */
  const handleDeleteClick = (user) => {
    setDeletingUserId(user.id);
    setShowDeleteDialog(true);
  };

  /**
   * Executar exclusão de usuário
   */
  const handleConfirmDelete = async () => {
    if (!deletingUserId) return;

    try {
      await userService.deleteUser(deletingUserId);
      showInfo("Usuário removido com sucesso", "Usuário Deletado");
      await fetchUsers();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erro ao deletar usuário";
      showError(errorMessage, "Erro");
    } finally {
      setShowDeleteDialog(false);
      setDeletingUserId(null);
    }
  };

  /**
   * Filtrar usuários por busca
   */
  const filteredUsers = users.filter((user) => {
    const query = searchQuery.toLowerCase();
    const email = (user.email || "").toLowerCase();
    const role = (user.role || "").toLowerCase();
    return email.includes(query) || role.includes(query);
  });

  /**
   * Retornar badge de role com cor
   */
  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: {
        label: "Administrador",
        className: "bg-red-100 text-red-800 border-red-200",
        icon: Shield,
      },
      manager: {
        label: "Gerente",
        className: "bg-blue-100 text-blue-800 border-blue-200",
        icon: UserRoundCog,
      },
      operator: {
        label: "Operador",
        className: "bg-green-100 text-green-800 border-green-200",
        icon: User,
      },
      user: {
        label: "Usuário",
        className: "bg-gray-100 text-gray-800 border-gray-200",
        icon: User,
      },
    };

    const config = roleConfig[role] || roleConfig.user;
    const IconComponent = config.icon;

    return (
      <Badge variant="outline" className={config.className}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Gerenciamento de Usuários
          </h1>
          <p className="text-gray-500 mt-1">
            {filteredUsers.length}{" "}
            {filteredUsers.length === 1 ? "usuário" : "usuários"} cadastrados
          </p>
        </div>

        <Button
          onClick={handleCreateUser}
          style={{ background: "#c9332C" }}
          onMouseEnter={(e) => (e.target.style.background = "#a02820")}
          onMouseLeave={(e) => (e.target.style.background = "#c9332C")}
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Usuário
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Buscar Usuários</CardTitle>
          <CardDescription>
            Pesquise por email ou função do usuário
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Digite o email ou função..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Usuários</CardTitle>
          <CardDescription>Gerencie os usuários do sistema</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando usuários...</p>
              </div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">
                {searchQuery
                  ? "Nenhum usuário encontrado"
                  : "Nenhum usuário cadastrado"}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {searchQuery
                  ? "Tente outro termo de busca"
                  : "Clique em 'Novo Usuário' para começar"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Função</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-mono text-sm text-gray-500">
                        #{user.id}
                      </TableCell>
                      <TableCell className="font-medium">
                        {user.email}
                      </TableCell>
                      <TableCell>{getRoleBadge(user.role)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteClick(user)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Excluir
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Form Modal */}
      <UserFormModal
        open={showModal}
        onOpenChange={setShowModal}
        user={editingUser}
        onSave={handleSaveUser}
        loading={savingUser}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este usuário? Esta ação não pode
              ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingUserId(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
